import React, { useState, useMemo } from 'react';
import {useDispatch} from "react-redux";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import {
  CloseRounded, UploadFileRounded, DoubleArrowRounded, TableChartRounded, CollectionsRounded, MoreVertRounded
} from "@mui/icons-material";
import {Tree, Card, Space, Row, Col, Modal, Input, notification, Divider, Popover, Menu} from 'antd';
import {renameFile, renameFolder, deleteResources} from "../../../api/filesApi";
import {formatBytes, transformResourcesToTreeData} from "../fileUtils";
import {useFileValidation} from '../../../hooks/useFileValidation';
import ImageGrid from "./ImageGrid";
import {IMAGE_EXTENSIONS} from "../fileUtils";
import Trail from './Trail';  // Import the new Trail component
import FileTable   from "./FileTable";


const { DirectoryTree } = Tree;

const filterResourcesByExtensions = (resources, extensions) => {
    const extensionSet = new Set(extensions.map(ext => ext.toLowerCase()));
    return resources.filter(resource => extensionSet.has(resource.path.split('.').pop().toLowerCase()));
};



const SiderButtons = ({viewMode, setViewMode})=>{
  return (
      <ButtonGroup size={'small'}>
        <Tooltip title={'List View'}>
          <Button variant={viewMode==='explorer'?'contained':'text'}
                  color={viewMode==='explorer'?'info':'primary'}
                  onClick={()=>setViewMode('explorer')}
          >
            <TableChartRounded/>
          </Button>
        </Tooltip>
        <Tooltip title={'Grid View'}>
          <Button variant={viewMode!=='explorer'?'contained':'text'}
                  color={viewMode!=='explorer'?'info':'primary'}
                  onClick={()=>setViewMode('grid')}
          >
            <CollectionsRounded/>
          </Button>
        </Tooltip>
      </ButtonGroup>
  )
};

const FileTableToolbar = ({currentPath, setIsCreateFolderModalVisible, onUpload})=>{
  return (
      <Space>
        <Button
            variant={'outlined'}
            color={'primary'}
            onClick={() => setIsCreateFolderModalVisible(true)}
        >
            Add New Folder
        </Button>
        <Button
                startIcon={<UploadFileRounded/>}
                variant={'contained'}
                onClick={ ()=>onUpload(currentPath) }  // Pass the folder path to upload
                color={'info'}
        >
          Upload Folders & Files
        </Button>
      </Space>
  )
}


const FileExplorer = ({ resources, handleSelect, maxSelect=null, whitelistExtensions=null,
                      onUpload}) => {
  const dispatch = useDispatch();
  const { validateSelection } = useFileValidation(maxSelect, whitelistExtensions);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false); // New state for folder creation modal
  const [newFolderName, setNewFolderName] = useState(''); // New folder name
  const [newName, setNewName] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [viewMode, setViewMode] = useState('explorer'); // 'explorer' or 'grid'

  const imageResources = useMemo(() => filterResourcesByExtensions(resources, IMAGE_EXTENSIONS), [resources]);



  const imageUrls = imageResources.map(res => res.public_url);


  const showRenameModal = (path) => {
    setCurrentPath(path);
    setIsModalVisible(true);
  };

  const handleRename = (newName) => {
    const newPath = `${currentPath.substring(0, currentPath.lastIndexOf('/') + 1)}${newName}`;
    if (currentPath.endsWith('/')) {
      dispatch(renameFolder({ oldPath: currentPath, newPath }));
    } else {
      dispatch(renameFile({ oldPath: currentPath, newPath }));
    }
    setIsModalVisible(false);
  };
  const handleCreateFolder = () => {
        if (!newFolderName) {
            notification.error({ message: 'Folder name cannot be empty', duration: 2 });
            return;
        }


        const newPath = `${currentPath}${newFolderName}`;
        setCurrentPath(newPath); // Update the current path to include the new folder
        setIsCreateFolderModalVisible(false); // Close the modal
        setNewFolderName(''); // Reset folder name input
    };

  const inRelativePath = (resourcePath)=>{
    const relpath = resourcePath.split('/').slice(2).join('/');
    return (relpath.startsWith(currentPath))
  }

  const handleDelete = (paths) => {
    dispatch(deleteResources({ paths }));
  };

  const onSelect = (selectedKeys, info) => {
    if (!info.node.isLeaf) { // Ensure action only on folders
      setCurrentPath(info.node.key)
    }
  };

  const onTableRowSelect = (selectedKeys, selectedRows) => {
    const errors = validateSelection(selectedRows.map(row => row.path));
    if (errors.length > 0) {
      notification.error({
        message: 'Validation Error',
        description: errors.join(' '),
        duration: 2,
      });
      return;
    }
    setSelectedRowKeys(selectedKeys);
  };
  const onNavigate = (newPath) => {
    setCurrentPath(newPath)
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onTableRowSelect,
  };

  const treeData = transformResourcesToTreeData(resources);
  const { filteredResources, folderNames } = useMemo(() => {
    const folders = new Set();
    const files = [];

    resources.forEach(resource => {
      const resourcePath = resource.path.split('/').slice(2).join('/');
      const parts = resourcePath.split('/');

      if (currentPath === '') {
        // At root level
        if (parts.length > 1) {
          folders.add(parts[0]);
        } else {
          files.push(resource);
        }
      } else {
        // In a subfolder
        if (resourcePath.startsWith(currentPath)) {
          const relativePath = resourcePath.slice(currentPath.length).split('/').filter(Boolean);
          if (relativePath.length === 0) {
            // This is the current folder itself, skip it
            return;
          } else if (relativePath.length === 1) {
            // Direct child
            if (resourcePath.endsWith('/')) {
              folders.add(relativePath[0]);
            } else {
              files.push(resource);
            }
          } else {
            // Nested folder
            folders.add(relativePath[0]);
          }
        }
      }
    });

    const folderResources = Array.from(folders).map(folderName => ({
      path: `${currentPath}${folderName}/`,
      name: folderName,
      isFolder: true,
      // Add any other necessary properties for folders
    }));

    return {
      filteredResources: [...folderResources, ...files],
      folderNames: Array.from(folders)
    };
  }, [resources, currentPath]);

  const handleFolderNav = (folderPath) => {
    setCurrentPath(folderPath);
  };
  return (
    <Row justify={'space-between'} wrap={false}>
      <Col flex={'0 0 200px'} style={{background: '#FFF', borderRadius: 8}}>
        <div className={'p-3'}>
          <SiderButtons viewMode={viewMode} setViewMode={setViewMode}/>
          <Divider/>
          <DirectoryTree style={{width: 250, padding: 0, background: 'transparent'}} onSelect={onSelect} treeData={treeData} defaultExpandAll={true} />
        </div>
      </Col>
      <Col flex={'1'} style={{marginLeft: '5px'}}>
        <Trail currentPath={currentPath} onNavigate={onNavigate} />

        {viewMode==='explorer' && (
            <>
              <Row>
          {handleSelect && (
                <Button
                    variant={selectedRowKeys.length > 0 ? 'contained' : 'outlined'}
                    color={selectedRowKeys.length > 0 ? 'primary' : 'error'}
                    endIcon={selectedRowKeys.length > 0 ? <DoubleArrowRounded/> : <CloseRounded/>}
                    onClick={() => {
                      if (selectedRowKeys.length > 0) {
                        handleSelect(selectedRowKeys);
                      } else {
                        setIsModalVisible(false)
                      }
                    }}
                >
                  {selectedRowKeys.length > 0 ? 'Continue With Selected' : "Close and Exit"}
                </Button>
          )}
        </Row>
        <Card bordered={false}
              extra={
                <FileTableToolbar currentPath={currentPath} setIsCreateFolderModalVisible={setIsCreateFolderModalVisible} onUpload={onUpload}/>
              }
        >
          <FileTable resources={filteredResources}
                     rowSelection={rowSelection}
                     showRenameModal={showRenameModal}
                     handleDelete={handleDelete}
                     handleFolderNav={handleFolderNav}
          />
        </Card>
      </>
        )}
        {viewMode==='grid' && (
            <ImageGrid imageUrls={imageUrls}/>

        )}
      </Col>
      <Modal
          title="Rename File/Folder"
          open={isModalVisible}
          onOk={() => handleRename(newName)}
          onCancel={() => setIsModalVisible(false)}
        >
          <Input placeholder="Enter new name" value={newName} onChange={(e) => setNewName(e.target.value)} />
        </Modal>
      <Modal
                title="Create New Folder"
                open={isCreateFolderModalVisible}
                onOk={handleCreateFolder}
                onCancel={() => setIsCreateFolderModalVisible(false)}
            >
                <Input
                    placeholder="Enter folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                />
      </Modal>
    </Row>
  );
};

export default FileExplorer;
